/**
 * CSV file naming for the ASK AI CSV download feature.
 *
 * Two pure functions that turn the inputs of one download activation into the
 * name handed to the anchor's `download` attribute in `downloadCsvFile.ts`.
 * Nothing here reads the clock, the DOM, or React state: the instant is
 * injected through `CsvFileNameParts.now`, so a name is deterministic and can
 * be asserted directly in tests (Req 4.1-4.4, 5.3).
 *
 * `sanitizeFileName` runs over the whole assembled name rather than over the
 * conversation identifier alone. That ordering is what makes the returned
 * string safe to place in a `download` attribute: a path separator, a quote, a
 * carriage return, or a line feed arriving through a conversation identifier
 * cannot reach the caller, because no unsanitized character survives the last
 * step of `buildCsvFileName`.
 */

import { CsvFileNameParts } from "./types";

/**
 * Substituted for the `conversation_id` portion of a name when a conversation
 * identifier is unavailable at download time (Req 4.4).
 */
const UNSAVED_CONVERSATION_ID = "unsaved";

/**
 * Every character a CSV file name may not hold. Applied after lowercasing, so
 * the class needs no uppercase range: the only characters that survive are
 * lowercase Latin letters, digits, periods, underscores, and hyphens
 * (Req 4.3). The hyphen sits last in the class, where it is literal.
 */
const DISALLOWED_FILE_NAME_CHARS = /[^a-z0-9._-]/g;

/** Decimal text of `value`, left-padded with zeros to at least `width`. */
function padNumber(value: number, width: number): string {
  return String(value).padStart(width, "0");
}

/**
 * `YYYYMMDD-HHmmss` for an instant, in Coordinated Universal Time (Req 4.2).
 *
 * Read through the UTC getters, never the local-time ones, so the same instant
 * names the same file regardless of the browser's time zone. `getUTCMonth` is
 * zero-based, hence the `+ 1`, and every component is padded to its field
 * width so the portion is fixed-length and sorts chronologically as text.
 */
function formatUtcTimestamp(now: Date): string {
  const year = padNumber(now.getUTCFullYear(), 4);
  const month = padNumber(now.getUTCMonth() + 1, 2);
  const day = padNumber(now.getUTCDate(), 2);
  const hours = padNumber(now.getUTCHours(), 2);
  const minutes = padNumber(now.getUTCMinutes(), 2);
  const seconds = padNumber(now.getUTCSeconds(), 2);
  return `${year}${month}${day}-${hours}${minutes}${seconds}`;
}

/**
 * `candidate` restricted to lowercase Latin letters, digits, hyphens,
 * underscores, and periods, with every other character replaced by a single
 * underscore (Req 4.3).
 *
 * Lowercasing happens first, so an uppercase letter is folded into the allowed
 * set rather than replaced. Replacement is one-for-one on characters, so the
 * result has the same length as the lowercased input.
 *
 * @param candidate Assembled file name, or any fragment of one.
 * @returns The restricted name. Never holds a path separator, a quote, a
 *   carriage return, a line feed, a space, or any non-ASCII character.
 */
export function sanitizeFileName(candidate: string): string {
  return candidate.toLowerCase().replace(DISALLOWED_FILE_NAME_CHARS, "_");
}

/**
 * File name for one downloaded table, following the pattern
 * `finn_table_{conversation_id}_m{message_index}_t{table_index}_{YYYYMMDD-HHmmss}.csv`
 * (Req 4.1).
 *
 * A missing, empty, or whitespace-only `conversationId` is replaced by the
 * literal `unsaved` (Req 4.4) — whitespace-only included, so an identifier of
 * spaces names the file `unsaved` rather than a run of underscores. The
 * timestamp comes from the UTC getters of the injected instant (Req 4.2).
 *
 * The `tableIndex` is what keeps the names of two tables in one answer apart
 * (Req 5.3): the rest of the name is shared by every table of a message
 * converted at the same instant, and distinct indexes appear verbatim in the
 * `t` portion, so distinct indexes give distinct names. No further uniqueness
 * machinery is involved.
 *
 * The assembled name passes through `sanitizeFileName` as the last step
 * (Req 4.3). The pattern's own characters — letters, digits, `_`, `-`, and the
 * `.` of the extension — are all in the allowed set, so sanitization leaves
 * the skeleton intact and only rewrites characters that came in through the
 * conversation identifier.
 *
 * @param parts Conversation identifier, message index, table index, and the
 *   instant to stamp.
 * @returns The sanitized file name, ending in `.csv`.
 */
export function buildCsvFileName(parts: CsvFileNameParts): string {
  const { conversationId, messageIndex, tableIndex, now } = parts;

  const hasConversationId =
    conversationId !== undefined &&
    conversationId !== null &&
    conversationId.trim() !== "";
  const conversation = hasConversationId
    ? conversationId
    : UNSAVED_CONVERSATION_ID;

  const timestamp = formatUtcTimestamp(now);
  const name =
    `finn_table_${conversation}` +
    `_m${messageIndex}_t${tableIndex}_${timestamp}.csv`;

  return sanitizeFileName(name);
}
